/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Printer } from 'lucide-react';

/**
 * ClassReportDialog Component
 *
 * Generates a professional, print-friendly class performance report.
 * Shows overall class statistics and individual student summaries.
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close handler
 * @param {Object} props.gradebookData - Full gradebook data
 * @param {function} props.calculateStudentGrade - Function to calculate final grade
 */
const ClassReportDialog = ({ open, onClose, gradebookData, calculateStudentGrade }) => {
  const printRef = useRef(null);

  if (!gradebookData) return null;

  // Calculate class statistics
  const classStats = {
    cohortName: gradebookData.cohort?.name || 'Class',
    studentCount: gradebookData.students?.length || 0,
    assignmentCount: gradebookData.assignments?.length || 0,
    classAverage: 0,
    passRate: 0,
    gradeDistribution: {
      'A (90-100%)': 0,
      'B (80-89%)': 0,
      'C (70-79%)': 0,
      'D (60-69%)': 0,
      'F (0-59%)': 0
    }
  };

  const studentSummaries = [];

  if (gradebookData.students && gradebookData.students.length > 0) {
    let totalGrades = 0;
    let passingStudents = 0;

    gradebookData.students.forEach(student => {
      const { finalGrade, letterGrade } = calculateStudentGrade(student.id);
      totalGrades += finalGrade;

      if (finalGrade >= 70) passingStudents++;

      // Grade distribution
      if (finalGrade >= 90) classStats.gradeDistribution['A (90-100%)']++;
      else if (finalGrade >= 80) classStats.gradeDistribution['B (80-89%)']++;
      else if (finalGrade >= 70) classStats.gradeDistribution['C (70-79%)']++;
      else if (finalGrade >= 60) classStats.gradeDistribution['D (60-69%)']++;
      else classStats.gradeDistribution['F (0-59%)']++;

      // Count completed assignments
      const completedAssignments = gradebookData.entries?.filter(e =>
        e.student_id === student.id && e.percentage !== null
      ).length || 0;

      studentSummaries.push({
        name: `${student.first_name} ${student.last_name}`,
        studentId: student.student_id,
        finalGrade,
        letterGrade,
        completedAssignments
      });
    });

    classStats.classAverage = Math.round((totalGrades / gradebookData.students.length) * 10) / 10;
    classStats.passRate = Math.round((passingStudents / gradebookData.students.length) * 100);
  }

  // Sort students by grade (high to low)
  studentSummaries.sort((a, b) => b.finalGrade - a.finalGrade);

  // Calculate category statistics
  const categoryStats = {};
  if (gradebookData.assignments && gradebookData.entries) {
    gradebookData.assignments.forEach(assignment => {
      const category = assignment.category_name || 'Uncategorized';
      if (!categoryStats[category]) {
        categoryStats[category] = {
          assignmentCount: 0,
          averageGrade: 0,
          totalGrades: 0,
          gradeCount: 0
        };
      }

      categoryStats[category].assignmentCount++;

      // Calculate average for this assignment across all students
      const assignmentGrades = gradebookData.entries.filter(e =>
        e.assignment_id === assignment.id && e.percentage !== null
      );

      assignmentGrades.forEach(grade => {
        categoryStats[category].totalGrades += grade.percentage || 0;
        categoryStats[category].gradeCount++;
      });
    });

    Object.keys(categoryStats).forEach(category => {
      if (categoryStats[category].gradeCount > 0) {
        categoryStats[category].averageGrade = Math.round(
          (categoryStats[category].totalGrades / categoryStats[category].gradeCount) * 10
        ) / 10;
      }
    });
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = printRef.current.innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Class Performance Report - ${classStats.cohortName}</title>
          <style>
            @media print {
              @page {
                size: letter portrait;
                margin: 0.5in;
              }
            }

            body {
              font-family: 'Arial', 'Helvetica', sans-serif;
              line-height: 1.4;
              color: #1f2937;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 20px;
              background: white;
            }

            .report-header {
              text-align: center;
              border-bottom: 3px solid #2563eb;
              padding-bottom: 15px;
              margin-bottom: 25px;
            }

            .report-header h1 {
              font-size: 28px;
              color: #1e40af;
              margin: 0 0 5px 0;
            }

            .report-header h2 {
              font-size: 20px;
              color: #475569;
              margin: 5px 0;
              font-weight: normal;
            }

            .report-date {
              font-size: 13px;
              color: #64748b;
              margin-top: 8px;
            }

            .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
              margin-bottom: 25px;
            }

            .stat-card {
              background: linear-gradient(135deg, #f8fafc 0%, #e5e7eb 100%);
              padding: 15px;
              border-radius: 8px;
              text-align: center;
              border: 1px solid #cbd5e1;
            }

            .stat-value {
              font-size: 32px;
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 5px;
            }

            .stat-label {
              font-size: 13px;
              color: #64748b;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .section {
              margin-bottom: 25px;
              page-break-inside: avoid;
            }

            .section-title {
              font-size: 18px;
              font-weight: 600;
              color: #1e40af;
              border-bottom: 2px solid #e5e7eb;
              padding-bottom: 8px;
              margin-bottom: 15px;
            }

            .distribution-grid {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 10px;
              margin-bottom: 20px;
            }

            .distribution-card {
              background: #f8fafc;
              padding: 12px;
              border-radius: 6px;
              text-align: center;
              border: 1px solid #e5e7eb;
            }

            .distribution-grade {
              font-size: 14px;
              font-weight: 600;
              color: #475569;
              margin-bottom: 8px;
            }

            .distribution-count {
              font-size: 28px;
              font-weight: bold;
              color: #1e40af;
            }

            .distribution-percent {
              font-size: 12px;
              color: #64748b;
              margin-top: 4px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }

            thead {
              background: #f1f5f9;
            }

            th {
              text-align: left;
              padding: 10px;
              font-weight: 600;
              color: #475569;
              border-bottom: 2px solid #cbd5e1;
            }

            td {
              padding: 8px 10px;
              border-bottom: 1px solid #e5e7eb;
            }

            .text-center {
              text-align: center;
            }

            .text-right {
              text-align: right;
            }

            .grade-cell {
              font-weight: 600;
            }

            .grade-a {
              color: #059669;
            }

            .grade-b {
              color: #2563eb;
            }

            .grade-c {
              color: #d97706;
            }

            .grade-d, .grade-f {
              color: #dc2626;
            }

            .category-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
              gap: 12px;
              margin-bottom: 20px;
            }

            .category-card {
              background: #f8fafc;
              padding: 15px;
              border-radius: 6px;
              border-left: 4px solid #3b82f6;
            }

            .category-card h4 {
              margin: 0 0 10px 0;
              font-size: 14px;
              color: #1e40af;
            }

            .category-stat {
              display: flex;
              justify-content: space-between;
              font-size: 12px;
              color: #64748b;
              margin: 5px 0;
            }

            .category-stat strong {
              color: #1f2937;
            }

            .footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #e5e7eb;
              font-size: 11px;
              color: #64748b;
              text-align: center;
            }

            @media print {
              body {
                padding: 0;
              }

              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Class Performance Report</DialogTitle>
            <Button onClick={handlePrint} size="sm" variant="outline">
              <Printer className="h-4 w-4 mr-2" />
              Print Report
            </Button>
          </div>
        </DialogHeader>

        {/* Report Content */}
        <div ref={printRef} className="report-content">
          {/* Header */}
          <div className="report-header">
            <h1>Class Performance Report</h1>
            <h2>{classStats.cohortName}</h2>
            <div className="report-date">
              Generated: {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>

          {/* Overall Statistics */}
          <div className="section">
            <div className="section-title">Overall Statistics</div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{classStats.studentCount}</div>
                <div className="stat-label">Total Students</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{classStats.assignmentCount}</div>
                <div className="stat-label">Total Assignments</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{classStats.classAverage}%</div>
                <div className="stat-label">Class Average</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{classStats.passRate}%</div>
                <div className="stat-label">Pass Rate (≥70%)</div>
              </div>
            </div>
          </div>

          {/* Grade Distribution */}
          <div className="section">
            <div className="section-title">Grade Distribution</div>
            <div className="distribution-grid">
              {Object.entries(classStats.gradeDistribution).map(([grade, count]) => (
                <div key={grade} className="distribution-card">
                  <div className="distribution-grade">{grade}</div>
                  <div className="distribution-count">{count}</div>
                  <div className="distribution-percent">
                    {classStats.studentCount > 0 ? Math.round((count / classStats.studentCount) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Performance */}
          {Object.keys(categoryStats).length > 0 && (
            <div className="section">
              <div className="section-title">Performance by Category</div>
              <div className="category-grid">
                {Object.entries(categoryStats).map(([category, stats]) => (
                  <div key={category} className="category-card">
                    <h4>{category}</h4>
                    <div className="category-stat">
                      <span>Assignments:</span>
                      <strong>{stats.assignmentCount}</strong>
                    </div>
                    <div className="category-stat">
                      <span>Class Average:</span>
                      <strong>{stats.averageGrade}%</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Student Summary Table */}
          <div className="section">
            <div className="section-title">Student Performance Summary</div>
            {studentSummaries.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Student Name</th>
                    <th>Student ID</th>
                    <th className="text-center">Completed</th>
                    <th className="text-center">Final Grade</th>
                    <th className="text-center">Letter</th>
                  </tr>
                </thead>
                <tbody>
                  {studentSummaries.map((student, index) => {
                    const letterClass = student.letterGrade[0].toLowerCase();
                    const gradeClass = `grade-${letterClass}`;

                    return (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>{student.name}</td>
                        <td>{student.studentId || 'N/A'}</td>
                        <td className="text-center">
                          {student.completedAssignments} / {classStats.assignmentCount}
                        </td>
                        <td className={`text-center grade-cell ${gradeClass}`}>
                          {Math.round(student.finalGrade)}%
                        </td>
                        <td className={`text-center grade-cell ${gradeClass}`}>
                          {student.letterGrade}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div style={{textAlign: 'center', padding: '20px', color: '#94a3b8'}}>
                No student data available
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="footer">
            <p>This report was generated electronically and reflects class performance as of the date shown above.</p>
            <p style={{marginTop: '5px'}}>Individual student reports are available through the gradebook interface.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClassReportDialog;